import type { Item } from "@/lib/types";
import { getLocationTier, getUserStateAndLGA } from "@/lib/locationHierarchy";
import {
  filterItemsByDiscoveryGranularity,
  resolveEffectiveDiscoveryGranularity,
  type HomepageDiscoveryGranularity,
} from "@/lib/items/homepageDiscoveryGranularity";

/**
 * Inputs that decide whether an item is eligible to appear in the buyer's
 * market browse. Mirrors the location-reach rules the market hook applies for
 * the location-based sorts.
 */
export interface MarketEligibilityContext {
  /** Buyer's resolved location, or null when unknown. */
  userLocation: {
    areaName?: string | null;
    formattedAddress?: string | null;
  } | null;
  /** Active sort. Discovery reach only constrains `newest` / `proximity`. */
  sortBy: string;
  /** When false, items outside the buyer's location tiers are dropped. */
  includeOtherLocationTier: boolean;
  /** How far out (neighborhood/lga/state) the browse reaches. */
  granularity: HomepageDiscoveryGranularity;
  /**
   * Widen the reach one tier at a time (e.g. LGA → state) when the configured
   * granularity leaves fewer than `minCount` eligible items. Mirrors the
   * homepage shelf so both surfaces surface the whole state before spillover
   * instead of stopping at a half-empty page. Defaults to off for callers that
   * want the fixed configured reach.
   */
  autoWiden?: boolean;
  /** Target eligible count before auto-widen stops. Ignored when autoWiden is off. */
  minCount?: number;
}

function stripOtherTierIfDisabled<T extends Item>(
  items: T[],
  userLocation: MarketEligibilityContext["userLocation"],
  includeOtherLocationTier: boolean,
): T[] {
  if (includeOtherLocationTier) return items;
  if (!userLocation) return items;
  const { state: userState, lga: userLGA } = getUserStateAndLGA({
    areaName: userLocation.areaName ?? "",
    formattedAddress: userLocation.formattedAddress ?? undefined,
  });
  const areaName = userLocation.areaName ?? "";
  return items.filter(
    (item) =>
      getLocationTier(item.location ?? "", areaName, userState, userLGA) !==
      "other",
  );
}

/**
 * Single owner of "which items may the buyer see" for the market browse.
 *
 * This is the membership/count source of truth (fixes R1/R2): the results
 * summary, empty-state decision, and rendered list must all derive from this
 * set so the count never disagrees with what actually renders. Sorting and
 * pagination happen downstream and never change membership.
 *
 * Filtering here is distance-independent, so callers can compute it
 * synchronously (no waiting on the distance-matrix call).
 */
export function selectEligibleMarketItems<T extends Item>(
  items: T[],
  ctx: MarketEligibilityContext,
): T[] {
  const {
    userLocation,
    sortBy,
    includeOtherLocationTier,
    granularity,
    autoWiden = false,
    minCount = 0,
  } = ctx;

  // Discovery reach only applies to the location-based sorts; other sorts show
  // the full fetched pool.
  if (!userLocation?.areaName?.trim()) return items;
  if (sortBy !== "newest" && sortBy !== "proximity") return items;

  const stripped = stripOtherTierIfDisabled(
    items,
    userLocation,
    includeOtherLocationTier,
  );

  const locationForHierarchy = {
    areaName: userLocation.areaName ?? "",
    formattedAddress: userLocation.formattedAddress ?? undefined,
  };

  // Widen LGA → state (etc.) when the configured reach is too thin, so the buyer
  // gets the rest of their state before any out-of-state spillover — matching
  // the homepage shelf's behavior. Single owner for market reach lives here.
  const effectiveGranularity = resolveEffectiveDiscoveryGranularity(
    stripped,
    locationForHierarchy,
    {
      configured: granularity,
      autoWiden,
      minCount,
      includeOtherLocationTier,
    },
  );

  return filterItemsByDiscoveryGranularity(
    stripped,
    locationForHierarchy,
    effectiveGranularity,
    includeOtherLocationTier,
  );
}
